"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; 
import styles from "../css/adminDashboard.module.css";
import Navbar from "../../../components/navbar";

import Footer from "../../../components/footer";
import StatsCard from "../../../components/stats-card";
import FilterButtons from "../../../components/universal-filter-tabs.jsx";
import Pagination from "../../../components/Pagination";
import Button from "../../../components/Button"; 
import { listCompanyApplications, approveCompanyApplication, rejectCompanyApplication } from "../../../api/admin/adminApi";

export default function AdminDashboard() {
  const [applications, setApplications] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // { app, mode: 'reject' | 'disable' }
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await listCompanyApplications();
        const data = res?.data || [];
        const mapped = data.map((item) => {
          let status;
          if (item.isactive === true) status = "Approved";
          else if (item.isactive === false) status = "Rejected";
          else status = "Pending";

          return {
            cipcregistrationnumber: item.cipcregistrationnumber,
            pharmacyName: item.companyname,
            applicantName: item.director
              ? `${item.director.firstname} ${item.director.surname}`
              : "",
            applicationDate: item.createdat
              ? new Date(item.createdat).toLocaleDateString("en-CA")
              : "",
            status,
            raw: item,
          };
        });
        setApplications(mapped);
      } catch (err) {
        setError("Failed to load applications");
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  const pendingCount = applications.filter((a) => a.status === "Pending").length;
  const approvedCount = applications.filter((a) => a.status === "Approved").length;
  const rejectedCount = applications.filter((a) => a.status === "Rejected").length;

  const filteredApplications = applications.filter((app) => {
    if (activeFilter === "Pending") return app.status === "Pending";
    if (activeFilter === "Approved") return app.status === "Approved";
    if (activeFilter === "Rejected") return app.status === "Rejected";
    return true; // "All"
  });

  const pageSize = 4; // 2 rows x 2 columns
  const totalPages = Math.max(1, Math.ceil(filteredApplications.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const pagedApplications = filteredApplications.slice(startIndex, startIndex + pageSize);

  const handleApprove = async (cipcregistrationnumber) => {
    try {
      await approveCompanyApplication(cipcregistrationnumber);
      setApplications((prev) =>
        prev.map((a) =>
          a.cipcregistrationnumber === cipcregistrationnumber
            ? { ...a, status: "Approved" }
            : a
        )
      );
    } catch (err) {
      // keep it simple: no popup component here
      console.error("Failed to approve application", err);
    }
  };

  const openConfirm = (app, mode) => {
    setConfirmAction({ app, mode });
  };

  const confirmReject = async () => {
    if (!confirmAction) return;
    const { app } = confirmAction;
    const { cipcregistrationnumber } = app;
    try {
      setActionLoading(true);
      setError("");
      if (confirmAction.mode === "disable") {
        // No dedicated disable endpoint; use reject to set inactive
        await rejectCompanyApplication(cipcregistrationnumber);
        setApplications((prev) =>
          prev.map((a) =>
            a.cipcregistrationnumber === cipcregistrationnumber
              ? { ...a, status: "Rejected" }
              : a
          )
        );
      } else {
        await rejectCompanyApplication(cipcregistrationnumber);
        setApplications((prev) =>
          prev.map((a) =>
            a.cipcregistrationnumber === cipcregistrationnumber
              ? { ...a, status: "Rejected" }
              : a
          )
        );
      }
      setConfirmAction(null);
    } catch (err) {
      console.error("Failed to change company status", err);
      setError(err?.response?.data?.message || err?.message || "Failed to change company status");
      setConfirmAction(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnable = async (cipcregistrationnumber) => {
    try {
      setActionLoading(true);
      setError("");
      // Enabling maps to approving in backend
      await approveCompanyApplication(cipcregistrationnumber);
      setApplications((prev) =>
        prev.map((a) =>
          a.cipcregistrationnumber === cipcregistrationnumber
            ? { ...a, status: "Approved" }
            : a
        )
      );
    } catch (err) {
      console.error("Failed to enable company", err);
      setError(err?.response?.data?.message || err?.message || "Failed to enable company");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.dashboardLayout}>
        <main className={styles.main}>
          <div className={styles.container}>
          <div className={styles.header}>
            <h1 className={styles.title}>Applications</h1>
          </div>

          {/* Stats Cards */}
          <div className={styles.statsGrid}>
            <StatsCard title="Pending Applications" value={pendingCount} icon="users" />
            <StatsCard title="Active Director/s" value={approvedCount} icon="monitor" />
            {/* <StatsCard title="Rejected Companies" value={rejectedCount} icon="alert-circle" /> */}
          </div>

          {/* Filter Tabs */}
          <div className={styles.filterTabsCenterWrap}>
            <FilterButtons
              buttons={["All", "Pending", "Approved", "Rejected"]}
              activeButton={activeFilter}
              onButtonClick={setActiveFilter}
              className={styles.filterTabsCenter}
            />
          </div>

          {/* Applications Grid */}
          {loading && <div className={styles.loading}>Loading applications...</div>}
          {error && !loading && <div className={styles.error}>{error}</div>}
          <div className={styles.applicationsSection}>
            {!loading && !error && pagedApplications.map((app, index) => (
              <div key={index} className={styles.card}>
                <div className={styles.cardContent}>
                  <div className={styles.cardHeader}>
                    <div>
                      <h3 className={styles.cardTitle}>{app.pharmacyName}</h3>
                      {/* <p className={styles.infoItem}>{app.applicantName}</p> */}
                    </div>
                    <span className={styles.badge}>{app.status}</span>
                  </div>

                  <div className={styles.info}>
                    <p className={styles.infoItem}>
                      <span className={styles.label}>Director:</span>{" "}
                      <span>{app.applicantName || "N/A"}</span>
                    </p>
                    <p className={styles.infoItem}>
                      <span className={styles.label}>Application Date:</span>{" "}
                      <span>{app.applicationDate || "N/A"}</span>
                    </p>
                  </div>

                  <div className={styles.actions}>
                    {app.status === "Pending" && (
                      <>
                        <Button
                          variant="approve"
                          size="sm"
                          disabled={actionLoading}
                          onClick={() =>
                            handleApprove(app.cipcregistrationnumber)
                          }
                        >
                          {actionLoading ? 'Working…' : 'Approve'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={actionLoading}
                          onClick={() => openConfirm(app, "reject")}
                        >
                          {actionLoading ? 'Working…' : 'Reject'}
                        </Button>
                      </>
                    )}

                    {app.status === "Approved" && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => openConfirm(app, "disable")}
                      >
                        {actionLoading ? 'Working…' : 'Disable'}
                      </Button>
                    )}

                    {(app.status === "Rejected" || app.status === "Disabled") && (
                      <Button
                        variant="approve"
                        size="sm"
                        disabled={actionLoading}
                        onClick={() => handleEnable(app.cipcregistrationnumber)}
                      >
                        {actionLoading ? 'Working…' : 'Enable'}
                      </Button>
                    )}

                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() =>
                        navigate("/view-pharmacy-app", {
                          state: { application: app.raw, status: app.status },
                        })
                      }
                    >
                      View More
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className={styles.paginationContainer}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </main>
      {confirmAction && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmDialog}>
            <p>
              {confirmAction.mode === "disable" ? (
                <>
                  Are you sure you want to disable the company {" "}
                  <strong>{confirmAction.app.pharmacyName}</strong>?
                </>
              ) : (
                <>
                  Are you sure you want to reject the application for {" "}
                  <strong>{confirmAction.app.pharmacyName}</strong>?
                </>
              )}
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmButton}
                disabled={actionLoading}
                onClick={confirmReject}
              >
                {actionLoading ? 'Working…' : 'Confirm'}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                disabled={actionLoading}
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  </div>
  );
}
